import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, FileText, Trash2, X, Eye, Lock, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import * as pdfjs from "pdfjs-dist/build/pdf";

pdfjs.GlobalWorkerOptions.workerSrc =
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * Document Vault — master library of BLANK templates only.
 *
 * The Vault is intentionally read-only:
 *  • Upload a blank PDF template → becomes reusable across every deal.
 *  • Click any template → opens a read-only preview modal.
 *  • Smart Fill and Send-for-Signature live exclusively on the Transaction
 *    page — pick a template there via "Add from Vault" to get a
 *    deal-scoped copy you can edit and sign.
 */
export default function Documents() {
  const [templates, setTemplates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { id, name }
  const [query, setQuery] = useState("");
  // Bulk-upload progress. `{ total, done, current }` while the queue
  // is running so the button can show "3 of 12 uploaded · Bylaws.pdf".
  const [bulk, setBulk] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // Two-tap delete confirm — first tap arms (turns the icon red), second
  // tap fires. Blur clears the armed state so an arm-then-scroll doesn't
  // leave a hot button primed for a fat-finger delete.
  const [armedDelete, setArmedDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/form-templates");
      setTemplates(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Fuzzy match over name + description. Character-subsequence match on
  // a lowercased query — every char in the query must appear in order
  // in the target. Handles "arzrpc" → "Arizona-Residential-Real-Estate-
  // Purchase-Contract" the way agents expect from a keystroke-first
  // search box. Ranks by exact-substring first, then subsequence,
  // then falls through unchanged for ties.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    const subseq = (s) => {
      let i = 0;
      for (const c of s) {
        if (c === q[i]) i++;
        if (i === q.length) return true;
      }
      return false;
    };
    return templates
      .map(t => {
        const hay = `${t.name} ${t.description || ""}`.toLowerCase();
        let rank = 0;
        if (hay.startsWith(q)) rank = 3;
        else if (hay.includes(q)) rank = 2;
        else if (subseq(hay)) rank = 1;
        return { t, rank };
      })
      .filter(x => x.rank > 0)
      .sort((a, b) => b.rank - a.rank)
      .map(x => x.t);
  }, [templates, query]);

  // Bulk-upload driver. Uploads sequentially so a slow storage bucket
  // doesn't get hammered with 30 parallel PUTs (which the Supabase
  // free tier sometimes 429s). Skips non-PDFs silently with a toast
  // summary at the end so the queue doesn't dead-stop on a stray
  // .DS_Store or a .png.
  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name.replace(/\.pdf$/i, ""));
    await api.post("/form-templates", fd);
  };
  const uploadMany = async (files) => {
    const pdfs = files.filter(f => f.name.toLowerCase().endsWith(".pdf"));
    const skipped = files.length - pdfs.length;
    if (pdfs.length === 0) {
      toast.error("Vault accepts blank PDF templates only");
      return;
    }
    setBulk({ total: pdfs.length, done: 0, current: pdfs[0].name });
    let ok = 0, failed = [];
    for (let i = 0; i < pdfs.length; i++) {
      setBulk({ total: pdfs.length, done: i, current: pdfs[i].name });
      try { await uploadOne(pdfs[i]); ok++; }
      catch (e) { failed.push(pdfs[i].name); }
    }
    setBulk(null);
    await load();
    const parts = [`${ok} template${ok === 1 ? "" : "s"} added`];
    if (skipped) parts.push(`${skipped} non-PDF skipped`);
    if (failed.length) parts.push(`${failed.length} failed`);
    (failed.length ? toast.error : toast.success)(parts.join(" · "));
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Vault accepts blank PDF templates only");
      return;
    }
    setUploading(true);
    try {
      await uploadOne(file);
      toast.success("Template added to Vault");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Drag-and-drop across the ENTIRE page — hard for the agent to miss
  // the drop zone. Individual overlay reacts to `dragOver` state.
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { if (e.target === e.currentTarget) setDragOver(false); };
  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    // Support folder drops (DataTransferItem's webkitGetAsEntry) — most
    // agents drag a folder of PDFs, not a multi-select from Finder.
    const files = await filesFromDataTransfer(e.dataTransfer);
    if (files.length === 0) return;
    if (files.length === 1) return upload(files[0]);
    uploadMany(files);
  };

  const del = async (id) => {
    // Optimistic delete: the row disappears the instant the agent
    // clicks so the Vault feels instant even on slow storage rounds.
    // We snapshot the row, remove it locally, then POST. On failure
    // we splice the row back in AT ITS ORIGINAL INDEX (preserving
    // the list's created_at DESC ordering) and surface an error.
    const idx = templates.findIndex(t => t.id === id);
    if (idx < 0) return;
    const snapshot = templates[idx];
    setTemplates(prev => prev.filter(t => t.id !== id));
    setArmedDelete(null);
    try {
      await api.delete(`/form-templates/${id}`);
      toast.success("Document deleted successfully.");
    } catch (e) {
      setTemplates(prev => {
        const next = prev.slice();
        next.splice(idx, 0, snapshot);
        return next;
      });
      toast.error(e?.response?.data?.detail || "Could not delete — brought that row back for you.");
    }
  };

  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} className="relative">
      {dragOver && (
        <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center" data-testid="vault-drop-overlay">
          <div className="absolute inset-6 rounded-2xl border-4 border-dashed" style={{ borderColor: "var(--primary-hex)" }} />
          <div className="rounded-xl bg-white shadow-2xl px-8 py-6 text-center border border-slate-200">
            <Upload className="h-8 w-8 mx-auto text-slate-900" />
            <p className="mt-2 font-heading text-lg font-bold text-slate-900">Drop your PDFs</p>
            <p className="mt-1 text-xs text-slate-500">Folder or multi-select — we'll queue them up in order.</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-6 mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">iKEY Master Library</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Document Vault</h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Your master blank templates. Click any doc for a read-only preview.
            To fill or send for signature, open a transaction and use <span className="font-semibold text-slate-700">Add from Vault</span> —
            that creates a deal-specific copy Smart Fill & DocuSign can work on.
          </p>
        </div>
        <label
          className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white cursor-pointer shrink-0"
          style={{ background: "var(--primary-hex)" }}
          data-testid="vault-upload-btn"
        >
          <Upload className="h-4 w-4" />
          {bulk ? `${bulk.done + 1} of ${bulk.total} · ${bulk.current}` : (uploading ? "Uploading…" : "Upload templates")}
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={e => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              if (files.length === 1) upload(files[0]);
              else uploadMany(files);
              e.target.value = "";
            }}
            data-testid="vault-file-input"
          />
        </label>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search templates — try 'buyer' or 'arzrpc'…"
          className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-9 py-2 text-sm placeholder:text-slate-400 focus-brand"
          data-testid="vault-search"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5"
            aria-label="Clear search"
            data-testid="vault-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}
        {!loading && templates.length === 0 && (
          <div className="p-12 text-center" data-testid="vault-empty">
            <FileText className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-3 font-heading text-lg font-semibold text-slate-900">Your Vault is empty</p>
            <p className="mt-1 text-sm text-slate-500">Upload a blank PDF contract to get started — or drop a folder of PDFs anywhere on the page.</p>
          </div>
        )}
        {!loading && templates.length > 0 && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="vault-search-empty">
            No templates match <b className="text-slate-900">"{query}"</b>.
          </div>
        )}
        {filtered.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setPreview({ id: t.id, name: t.name })}
            className="w-full text-left px-6 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-4 hover:bg-slate-50 transition-colors"
            data-testid={`vault-row-${t.id}`}
          >
            <FileText className="h-5 w-5 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 text-sm truncate flex items-center gap-2">
                <span className="truncate">{t.name}</span>
                {t.is_acroform && (
                  <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-widest">
                    Smart-Fill ready
                  </span>
                )}
                {t.is_public && (
                  <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded uppercase tracking-widest">
                    iKEY gallery
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <Lock className="h-3 w-3" />
                Master template · {t.field_count ?? (t.fields || []).length} field{(t.field_count ?? (t.fields || []).length) === 1 ? "" : "s"} · added {new Date(t.created_at).toLocaleDateString()}
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
              <Eye className="h-3.5 w-3.5" /> Preview
            </span>
            {!t.is_public && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (armedDelete === t.id) del(t.id);
                  else setArmedDelete(t.id);
                }}
                onBlur={() => armedDelete === t.id && setArmedDelete(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    if (armedDelete === t.id) del(t.id);
                    else setArmedDelete(t.id);
                  }
                }}
                className={`p-1 cursor-pointer rounded ${armedDelete === t.id ? "text-white bg-red-600" : "text-slate-400 hover:text-red-600"}`}
                data-testid={`vault-del-${t.id}`}
                aria-label={armedDelete === t.id ? "Confirm delete" : "Delete template"}
                title={armedDelete === t.id ? "Tap again to confirm" : "Delete template"}
              >
                <Trash2 className="h-4 w-4" />
              </span>
            )}
          </button>
        ))}
      </div>

      {preview && <TemplatePreviewModal templateId={preview.id} name={preview.name} onClose={() => setPreview(null)} />}
    </div>
  );
}

// Flatten a DataTransfer (drag+drop) into a flat File[] — walks nested
// directory entries (webkitGetAsEntry) so dropping a folder yields every
// PDF inside, not the folder itself. Falls back to `files` when the
// browser doesn't support the entry API.
async function filesFromDataTransfer(dt) {
  const items = dt.items ? Array.from(dt.items) : [];
  const entries = items.map(i => i.webkitGetAsEntry && i.webkitGetAsEntry()).filter(Boolean);
  if (entries.length === 0) return Array.from(dt.files || []);
  const out = [];
  const walk = async (entry) => {
    if (entry.isFile) {
      await new Promise((res) => entry.file((f) => { out.push(f); res(); }, res));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const readAll = () => new Promise((res) => reader.readEntries(res));
      let batch = await readAll();
      while (batch.length) {
        for (const e of batch) await walk(e);
        batch = await readAll();
      }
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

// ── Read-only preview modal ────────────────────────────────────────────────
// Renders the master template as static PDF pages. No fields, no actions —
// just eyes-only. Reinforces that the Vault is a library, not a workshop.

function TemplatePreviewModal({ templateId, name, onClose }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/form-templates/${templateId}`);
        const url = data.blank_url;
        const resp = await fetch(url);
        const buf = await resp.arrayBuffer();
        const bytes = new Uint8Array(buf.slice(0));
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        const out = [];
        const scale = 1.3;
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          out.push({ pageIndex: i - 1, width: viewport.width, height: viewport.height, imgUrl: canvas.toDataURL() });
        }
        if (!cancelled) { setPages(out); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError(e?.message || "Could not open template"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="vault-preview-modal">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Read-only preview
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 truncate">{name}</h3>
            <p className="mt-1 text-xs text-slate-500">
              To fill or sign this contract, open a transaction and use "Add from Vault".
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="vault-preview-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 bg-slate-100 max-h-[75vh] overflow-y-auto space-y-3">
          {loading && (
            <div className="text-center text-sm text-slate-500 py-16 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
            </div>
          )}
          {error && <div className="p-6 text-sm text-rose-700 bg-rose-50 rounded">Could not open: {error}</div>}
          {!loading && !error && pages.map(pg => (
            <div
              key={pg.pageIndex}
              className="relative mx-auto shadow ring-1 ring-slate-200 bg-white"
              style={{ width: pg.width, height: pg.height }}
              data-testid={`vault-preview-page-${pg.pageIndex}`}
            >
              <img src={pg.imgUrl} alt="" className="absolute inset-0 w-full h-full select-none pointer-events-none" draggable={false} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
